import { AuthenticationStrategy, Stark, isAuthenticationRequired } from '@mc-alberta/stark'
import {
  AuthenticationMethodType,
  AuthenticationReason,
  AuthenticationResult,
  AuthenticationStatus
} from '@mc-alberta/types'
import axios from 'axios'
import { CLIENT_ID, LOADERS, ROUTES } from '@/constants'
import router from '@/router'
import * as types from '@/store/mutation-types'
import { getTraceId } from '@/utils/traceId'

export const stark = {
  instance: null
}

/**
 * Returns a new copy of the store state.
 */
export const createState = () => ({
  /** Promise resolved with the initialization result. */
  initialization: null,
  /** The name of the route that was interrupted for authentication. */
  interruptedRoute: null,
  /** Authenticate promise resolution function. */
  authenticateResolve: () => {},
  isChallengeRequested: false
})

/**
 * Returns the current Stark instance.
 */
export const getStark = () => stark.instance

/**
 * Returns the challenge page route corresponding to the given strategy.
 */
export const getChallengeRoute = (strategy) => {
  switch (strategy) {
    case AuthenticationStrategy.MDES:
      return ROUTES.VERIFY_ISSUER
    case AuthenticationStrategy.THREE_DS:
    default:
      return ROUTES.VERIFY_ISSUER_CHALLENGE
  }
}

/**
 * Router guard for authentication pages.
 */
export const sessionRouteGuard = (strategy, next) => {
  return strategy === stark.instance?.strategy ? next() : next({ name: ROUTES.ENTRY })
}

const state = createState()

const getters = {
  /**
   * Display name of the issuing bank.
   */
  issuerName() {
    return (stark.instance?.authenticationMethod || stark.instance?.authenticationMethods[0])
      ?.methodAttributes?.identityProviderName
  }
}

const actions = {
  /**
   * Initializes and bootstraps the Stark instance.
   */
  initializeAuthentication({ commit, dispatch, state }, { maskedCard, cardToken }) {
    commit(types.SET_IS_CHALLENGE_REQUESTED, false)

    stark.instance = new Stark({
      accountReference: {
        srcDigitalCardId: maskedCard.srcDigitalCardId
      },
      authenticationContext: {
        authenticationReasons: [AuthenticationReason.ENROL_FINANCIAL_INSTRUMENT]
      },
      cardToken,
      srcCorrelationId: state.correlationId,
      // srcClientId: '2fb2cd53-a454-4818-9e65-e572240db922',
      srcClientId: CLIENT_ID,
      serviceId: 'SRC',
      suppressChallenge: false,
      traceId: getTraceId(),
      namespace: 'PMUI'
    })

    if (isAuthenticationRequired(maskedCard)) {
      dispatch('startAuthSession')
    }
  },

  /**
   * Initializes the authentication process in Stark.
   */
  startAuthSession({ commit }) {
    commit(
      types.SET_INITIALIZATION,
      (async () => {
        await stark.instance.bootstrapStrategies([
          AuthenticationMethodType.SMS_OTP,
          AuthenticationMethodType.EMAIL_OTP,
          AuthenticationMethodType.THREE_DS
        ])
        return stark.instance.initializeSession()
      })()
    )
  },

  /**
   * Performs cardholder authentication.
   * This will resolve once authentication is successfully completed.
   */
  async authenticate({ commit, dispatch, state, rootState }, cardToken) {
    const promise = new Promise((resolve) => commit(types.SET_AUTHENTICATE_RESOLVE, resolve))

    if (!stark.instance || !state.initialization) {
      const { newCard } = rootState.cards

      dispatch('initializeAuthentication', {
        maskedCard: newCard,
        cardToken
      })
    } else {
      stark.instance.cardToken = cardToken
    }

    dispatch('handleAuthSession', await state.initialization)

    return promise
  },

  /**
   * Handles the authentication status of the given session promise.
   */
  async handleAuthSession({ commit, dispatch, state }, promise) {
    const session = await promise

    switch (session.authenticationStatus) {
      case AuthenticationStatus.COMPLETE:
        return dispatch('handleAuthComplete', session)
      case AuthenticationStatus.CANCELLED:
        return dispatch('cancelAuthSession')
      case AuthenticationStatus.PENDING_CHALLENGE:
        commit(types.SET_IS_CHALLENGE_REQUESTED, true)
        return dispatch('routeAndStopAllLoaders', {
          name: getChallengeRoute(stark.instance.strategy),
          params: state.interruptedRoute.params
        })
      case AuthenticationStatus.PENDING_METHOD_SELECTION:
        return dispatch('routeAndStopAllLoaders', { name: ROUTES.VERIFY_OPTIONS })
      case AuthenticationStatus.CHALLENGE_FAILED:
      case AuthenticationStatus.EXPIRED:
        stark.instance.flush()
        return dispatch('routeAndStopAllLoaders', { name: ROUTES.ERROR })
      default:
        return dispatch('attemptNextAuthStrategy')
    }
  },

  /**
   * Completion handler of an authentication session.
   */
  async handleAuthComplete({ dispatch }, session) {
    if (session.authenticationResult === AuthenticationResult.AUTHENTICATED) {
      // need not be awaited
      stark.instance.bindSession()
      stark.instance.flush()
      return state.authenticateResolve()
    }

    return dispatch('attemptNextAuthStrategy')
  },

  /**
   * Attempts the next strategy in line.
   */
  attemptNextAuthStrategy({ dispatch }) {
    stark.instance.selectNextUsableStrategy()

    if (!stark.instance.strategy) {
      // all out of strategies
      return dispatch('routeAndStopAllLoaders', { name: ROUTES.ERROR })
    }

    dispatch('handleAuthSession', stark.instance.initializeSession())
  },

  /**
   * Cancels the challenge.
   *
   * This redirects back to the cards list if the user is logged in,
   * or back to the entry page if the user was creating a profile.
   */
  async cancelAuthSession({ dispatch, rootGetters }) {
    const route = { name: ROUTES.LIST_CARDS }

    if (!rootGetters.isLoggedIn) {
      route.name = ROUTES.ENTRY
      route.params = { logout: true }
    }
    stark.instance.flush()
    stark.instance = null
    router.replace(route)
    dispatch('stopAllLoaders', null)
  },

  /**
   * Navigates to the given route name.
   */
  routeAndStopAllLoaders({ dispatch }, route) {
    router.push(route)
    dispatch('stopAllLoaders', null)
  },

  /**
   * Axios interceptor for a failed XHR request.
   *
   * This invokes authentication when its response indicates that SCA is required,
   * and then replays that request with the updated card token once SCA is complete.
   */
  async intercept({ state, commit, dispatch, rootState, rootGetters }, error) {
    if (error.response?.data?.status !== 'SCA_REQUIRED') {
      return Promise.reject(error)
    }

    const interruptedLoader = rootGetters.activeLoader

    commit(types.SET_INTERRUPTED_ROUTE, router.currentRoute)
    dispatch('startLoader', LOADERS.VERIFY_DETAILS)
    await dispatch('authenticate', error.response.headers['x-src-card-token'])
    dispatch('stopLoader', LOADERS.VERIFY_DETAILS)
    dispatch('startLoader', interruptedLoader)

    if (stark.instance.authenticationResult !== AuthenticationResult.AUTHENTICATED) {
      return Promise.reject(error)
    }

    router.replace(state.interruptedRoute)

    // re-attempt the failed request, this time with the new card data
    commit(types.SET_NEW_CARD_DATA, {
      token: stark.instance.cardToken,
      card: rootState.cards.newCard
    })
    commit(types.SET_INTERRUPTED_ROUTE, null)

    error.config.headers['X-SRC-CARD-TOKEN'] = stark.instance.cardToken
    stark.instance = null

    return axios.request(error.config)
  }
}

const mutations = {
  /** Sets the `authenticate()` resolver function. */
  [types.SET_AUTHENTICATE_RESOLVE](state, authenticateResolve) {
    state.authenticateResolve = authenticateResolve
  },

  /** Sets the initialization promise. */
  [types.SET_INITIALIZATION](state, initialization) {
    state.initialization = initialization
  },

  /** Caches the interrupted route name. */
  [types.SET_INTERRUPTED_ROUTE](state, interruptedRoute) {
    state.interruptedRoute = interruptedRoute
  },

  /**
   * Sets the whether or not a challenge is needed
   *
   * @param {Object} state
   * @param {Boolean} isChallengeRequested
   */
  [types.SET_IS_CHALLENGE_REQUESTED](state, isChallengeRequested) {
    state.isChallengeRequested = isChallengeRequested
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
